/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener("DOMContentLoaded", () => {
  const listItems = document.querySelectorAll(".header__linklist-item");

  const config = {
    fontSize: 18,
    fontFamily: '"Source Sans Pro", sans-serif',
    textColor: "#777766",
    backgroundColor: "#FFFFFF",
    padding: 0,
    quality: 1.0,
    maxWidth: 200  // Add maximum width constraint
  };

  const convertTextToImage = (text, context, canvas, dpr) => {
    const font = `${config.fontSize}px ${config.fontFamily}`;
    context.font = font;

    // Measure text dimensions
    const textMetrics = context.measureText(text);
    const naturalWidth = Math.ceil(textMetrics.width + config.padding * 2);
    const naturalHeight = config.fontSize + config.padding * 2;

    // Constrain width if necessary
    const width = Math.min(naturalWidth, config.maxWidth);
    const height = naturalHeight;

    // Set dimensions accounting for device pixel ratio
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    
    // IMPORTANT: Set the CSS dimensions explicitly to prevent high-DPI scaling issues
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    // Scale and configure context
    context.scale(dpr, dpr);
    context.textRendering = "optimizeLegibility";
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    // Draw background
    context.fillStyle = config.backgroundColor;
    context.fillRect(0, 0, width, height);

    // Draw text
    context.font = font;
    context.fillStyle = config.textColor;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(text, width / 2, height / 2);

    return { width, height };
  };

  listItems.forEach((listItem) => {
    const link = listItem.querySelector(".header__linklist-link");
    if (!link) return;

    const text = link.textContent.trim();
    if (!text) return;

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", { alpha: false });
    const dpr = window.devicePixelRatio || 1;

    // Convert text to image
    const { width, height } = convertTextToImage(text, context, canvas, dpr);

    // Create and configure image element
    const img = new Image();
    img.src = canvas.toDataURL("image/png", config.quality);
    img.alt = text;
    img.title = text;
    
    // IMPORTANT: Set explicit dimensions on the image element
    img.width = width;
    img.height = height;
    
    // Force the image to maintain its dimensions
    img.style.width = `${width}px`;
    img.style.height = `${height}px`;
    img.style.maxWidth = "100%";  // Ensure image doesn't overflow container
    
    // Apply rendering optimizations
    img.style.imageRendering = "auto";
    img.style.webkitFontSmoothing = "antialiased";
    img.style.mozOsxFontSmoothing = "grayscale";

    // Replace text with image
    link.textContent = "";
    link.appendChild(img);
  });
});

//Klevu cart Recommendations
function addToCartKlevu(id, quantity) {
  // Show loading state if available in Focal theme
  const loadingOverlay = document.querySelector('.loading-overlay');
  if (loadingOverlay) {
    loadingOverlay.classList.add('active');
  }

  // Add item to cart using Shopify AJAX API
  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      id: id,
      quantity: quantity
    })
  })
  .then(response => response.json())
  .then(item => {
    // Update Klevu's cart data
    klevu_addtocart(id);
    
    // Update the mini-cart using Focal's methods
    if (typeof theme !== 'undefined' && theme.cart && typeof theme.cart.updateDrawer === 'function') {
      theme.cart.updateDrawer();
    } else {
      // Fallback for Focal theme - update cart and open drawer
      updateCartDrawer();
    }
    
    // Hide loading state
    if (loadingOverlay) {
      loadingOverlay.classList.remove('active');
    }
  })
  .catch(error => {
    console.error('Error adding product to cart:', error);
    
    // Hide loading state
    if (loadingOverlay) {
      loadingOverlay.classList.remove('active');
    }
  });
}

// Helper function to update Focal theme cart drawer
function updateCartDrawer() {
  fetch('?section_id=mini-cart')
    .then(response => response.text())
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find Focal's cart drawer content
      const cartDrawerContent = doc.querySelector('.mini-cart') || 
                                doc.querySelector('#mini-cart');
                                
      if (cartDrawerContent) {
        const targetElement = document.querySelector('.mini-cart') || 
                             document.querySelector('#mini-cart');
        if (targetElement) {
          targetElement.innerHTML = cartDrawerContent.innerHTML;
        }
      }
      
      // Update cart count
      updateCartCount();
      
      // Open cart drawer
      const cartDrawer = document.querySelector('.mini-cart') || 
                         document.querySelector('#mini-cart');
      if (cartDrawer) {
        cartDrawer.setAttribute('aria-hidden', 'false');
        cartDrawer.setAttribute("open", "");
      }
 
    })
    .catch(error => {
      console.error('Error refreshing cart drawer:', error);
    });
}

// Update the Klevu cart data and recommendations
function klevu_addtocart(id) {
  // STEP 1: Set page type to 'cart'
  klevu_page_meta.pageType = 'cart';
  
  // STEP 2: Add the product to cartRecords
  klevu_page_meta.cartRecords.push({
    'itemId': id,
    'itemGroupId': id.toString().split(':')[0] // Group ID is typically product ID without variant
  });
  
  // STEP 3: Update klevu.pageMeta
  klevu.pageMeta.update(klevu_page_meta, 1);
  
  // Initialize cart recommendations
  klevu.each(klevu.dom.find(".klevu-recs-addtocart"), function (key, recsTargetElement) {
    if (!recsTargetElement.id) {
      console.info("No Klevu RECS ID found on the targeted element!", recsTargetElement);
      return;
    }

    var found = false;
    klevu.each(klevu.recs.list, function(key, rec){
      if (rec.getScope().element === recsTargetElement) {
        found = true;
      }
    });

    if (!found) {
      klevu.recs.clone({
        recId: recsTargetElement.id,
        apiKey: klevu.getGlobalSetting(
          "recs.apiKey",
          klevu.getGlobalSetting("global.apiKey")
        ),
        element: recsTargetElement
      });
    } 
  });

  // Power up recommendations
  var recsList = klevu.recs.list;
  if (recsList && recsList.length) {
    klevu.each(recsList, function (key, recsObj) {
      if (recsObj.getScope().element.classList.contains('klevu-recs-addtocart')) {
        recsObj.powerUp();
      }
    });
  }
}

// Update cart count specific to Focal theme
function updateCartCount() {
  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      // Update all cart count elements in Focal theme, including the custom cart-count element
      const cartCountElements = document.querySelectorAll('.cart-link__bubble-num, .site-header__cart-count, .js-cart-count, .header__cart-count, cart-count');
      cartCountElements.forEach(elem => {
        elem.textContent = cart.item_count;
        
        // Toggle visibility of cart count bubbles
        const bubble = elem.closest('.cart-link__bubble') || elem;
        if (bubble && bubble !== elem) {
          bubble.classList.toggle('cart-link__bubble--visible', cart.item_count > 0);
        }
      });
    });
}

/*
function initializeKlevuRecs() {
  klevu.each(klevu.dom.find(".klevu-recs"), function (key, recsTarget) {
    if (klevu.isUndefined(recsTarget.recsObject)) {
      let box = klevu.recs.clone({
        recId: recsTarget.id,
        apiKey: klevu.getGlobalSetting("recs.apiKey", klevu.getGlobalSetting("global.apiKey")),
        element: recsTarget
      });
      box.powerUp();
    }
  });
}*/

function initializeKlevuRecs() {
  
  // Find all elements with the 'klevu-recs' class
  const recsTargets = klevu.dom.find(".klevu-recs");
  klevu.each(recsTargets, function (key, recsTarget) {
    if (klevu.isUndefined(recsTarget.recsObject)) {
      let box = klevu.recs.clone({
        recId: recsTarget.id,
        apiKey: klevu.getGlobalSetting("recs.apiKey", klevu.getGlobalSetting("global.apiKey")),
        element: recsTarget
      });
      box.powerUp();
    } else {
      //console.log("Recommendation object already exists for target:", recsTarget);
    }
  });
}

//fix for issue where cart recommendations are not displaying when page first loads. 
function initializeCartRecommendations() {
  // First, make sure we have current cart data
  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      // Update klevu_page_meta with current cart data
      //klevu_page_meta.pageType = "cart";
      klevu_page_meta.cartRecords = [];
      
      // Add all current cart items to klevu_page_meta
      if (cart.items && cart.items.length > 0) {
        cart.items.forEach(item => {
          klevu_page_meta.cartRecords.push({
            'itemId': item.variant_id.toString(),
            'itemGroupId': item.product_id.toString(),
            'itemName': item.title,
            'itemUrl': window.location.origin + item.url,
            'itemSalePrice': (item.price / 100).toString()
          });
        });
      }
      
      // Update klevu's page meta
      if (typeof klevu !== 'undefined' && klevu.pageMeta) {
        klevu.pageMeta.update(klevu_page_meta, 1);
      }
      
      // Now specifically initialize the recommendation widget
      const recWidget = document.getElementById('k-50583877-cb41-4aee-a972-73d3abdab04d');
      if (recWidget && typeof klevu !== 'undefined' && klevu.recs) {
        // Check if it's already initialized
        let alreadyInitialized = false;
        klevu.each(klevu.recs.list, function(key, rec) {
          if (rec.getScope().element.id === 'k-50583877-cb41-4aee-a972-73d3abdab04d') {
            alreadyInitialized = true;
            // Instead of using refresh, we'll reinitialize the widget
            rec.getScope().element.recsObject = undefined;
          }
        });
        
        // If not initialized or needs reinitializing, create and power up
        if (!alreadyInitialized) {
          let recBox = klevu.recs.clone({
            recId: 'k-50583877-cb41-4aee-a972-73d3abdab04d',
            apiKey: klevu.getGlobalSetting("recs.apiKey", klevu.getGlobalSetting("global.apiKey")),
            element: recWidget
          });
          
          // Set timeout to ensure the metadata is updated before powering up
          setTimeout(function() {
            recBox.powerUp();
          }, 100);
        }
      }
      
      // Also make sure the recommendations engine is initialized
      if (typeof klevu !== 'undefined' && klevu.recommendation && klevu.recommendation.base) {
        klevu.recommendation.base.init();
      }
    })
    .catch(error => {
      console.error('Error fetching cart data:', error);
    });
}



// MutationObserver to detect changes in the cart (or any other part of the page)
const observer = new MutationObserver(function(mutationsList) {
  mutationsList.forEach(function(mutation) {
    // Check if the cart or relevant elements changed
    if (mutation.type === "childList" || mutation.type === "attributes") {
      initializeKlevuRecs(); // Re-run the function when changes occur
    }
  });
});

// Target the container where the cart elements are updated
const cartElement = document.querySelector(".mini-cart"); 
  if (cartElement) {
  observer.observe(cartElement, {
    childList: true, // Observe added/removed elements
    attributes: true, // Observe attribute changes (e.g., quantity change)
    subtree: true // Observe all descendants
  });
}

// Hook Klevu recommendation add to cart buttons
document.addEventListener('DOMContentLoaded', function() {

  updateCartMetaData();
  initializeKlevuRecs();
  initializeCartRecommendations();
  // Listen for clicks on Klevu recommendation add to cart buttons
  document.body.addEventListener('click', function(event) {
    //const kleuvuAddToCartBtn = event.target.closest('.kuAddtocart, .klevu-add-to-cart, .product-form__add-button, .kuRECSAddToCartBtn');
    const kleuvuAddToCartBtn = event.target.closest('.kuAddtocart, .klevu-add-to-cart, .kuRECSAddToCartBtn');
    if (kleuvuAddToCartBtn) {
      //event.preventDefault();
      
      // Get variant ID from data attribute
      const variantId = kleuvuAddToCartBtn.dataset.id || 
                      kleuvuAddToCartBtn.getAttribute('data-id');
      
      if (variantId) {
        addToCartKlevu(variantId, 1);
      }
    }
  });


});

document.addEventListener('DOMContentLoaded', function() {
    const track = document.getElementById('carouselTrack');
    const prevBtn = document.getElementById('prevBtn');
    const nextBtn = document.getElementById('nextBtn');

    if (!track || !prevBtn || !nextBtn) return; // safety check

    let pos = 0;
    let itemWidth;
    let visibleItems;
    const totalItems = document.querySelectorAll('.sr-cat-item').length;

    function calcDimensions() {
        const item = document.querySelector('.sr-cat-item');
        if (!item) return;
        const style = window.getComputedStyle(item);
        itemWidth = item.offsetWidth + parseInt(style.marginRight || 20);
        const containerWidth = document.querySelector('.sr-cat-wrapper').offsetWidth;
        //visibleItems = Math.floor(containerWidth / itemWidth);
        visibleItems = Math.ceil(containerWidth / itemWidth);
    }

    function updateNav() {
        prevBtn.disabled = pos === 0;
        nextBtn.disabled = pos >= totalItems - visibleItems;
    }

    function slide(dir) {
        calcDimensions();
        const maxPos = totalItems - visibleItems;

        if (dir === 'next' && pos < maxPos) {
            pos++;
        } else if (dir === 'prev' && pos > 0) {
            pos--;
        }

        track.style.transform = `translateX(-${pos * itemWidth}px)`;
        updateNav();
    }

    prevBtn.addEventListener('click', () => slide('prev'));
    nextBtn.addEventListener('click', () => slide('next'));

    window.addEventListener('resize', () => {
        calcDimensions();
        pos = Math.min(pos, totalItems - visibleItems);
        track.style.transform = `translateX(-${pos * itemWidth}px)`;
        updateNav();
    });

    // Initialize
    calcDimensions();
    updateNav();
});

// -------------------------------------------------------------------------------------------------
// Subscription / selling-plan selector  (Amazon "Subscribe & Save" style)
// Two-tier chooser: One-time vs Subscribe radios + a "Ships every" frequency dropdown.
// A hidden `selling_plan` input carries the submitted value — "" for one-time, or the selected
// frequency plan id for a subscription — so the existing add-to-cart flow needs no changes.
// Prices and per-unit prices next to each row stay in sync with the selected variant.
//
// Selling plans attach per variant, so everything here is keyed on the selected variant rather than
// the product: which plans the dropdown offers, whether the component shows at all, and every price
// it renders. A product's variants can sit in two different selling plan groups, so a variant change
// can replace the whole plan set. See snippets/product-subscription-options.liquid for the two gates.
// -------------------------------------------------------------------------------------------------
if (!window.customElements.get('subscription-options')) {
  class SubscriptionOptions extends HTMLElement {
    connectedCallback() {
      try {
        this.prices = JSON.parse(this.querySelector('.subscription-options__data')?.textContent || '{}');
      } catch (e) {
        this.prices = {};
      }

      this.hiddenInput = this.querySelector('input[name="selling_plan"]');
      this.frequencySelect = this.querySelector('[data-frequency-select]');
      this.variantId = this.getAttribute('current-variant');
      // set when _updateAvailability drops to one-time on a non-subscribable variant, so that
      // fallback can be undone on the way back out without overriding a real customer choice
      this.forcedOneTime = false;
      // last delivery label the customer chose, used to re-find their frequency after a rebuild
      this.selectedLabel = null;
      this._rememberFrequency();

      this._onPurchaseTypeChange = this._onPurchaseTypeChange.bind(this);
      this._onFrequencyChange = this._onFrequencyChange.bind(this);
      this._onVariantChanged = this._onVariantChanged.bind(this);

      this.querySelectorAll('[data-purchase-type]').forEach((radio) => {
        radio.addEventListener('change', this._onPurchaseTypeChange);
      });
      if (this.frequencySelect) {
        this.frequencySelect.addEventListener('change', this._onFrequencyChange);
      }

      const form = document.getElementById(this.getAttribute('form-id'));
      if (form) {
        form.addEventListener('variant:changed', this._onVariantChanged);
      }

      // Establish the initial submitted value + row prices for the current variant. The frequency
      // options and the hidden attribute are already rendered server-side from that variant's
      // allocations, so there is no rebuild here, only the state that has to agree with them.
      this._updateAvailability();
      this._syncSellingPlan();
      this._updateBreakdown();
      this._refreshPrices();
      this._renderEta();
    }

    // The plan id currently chosen in the frequency dropdown (drives the "__current" price row),
    // or null when the dropdown is empty because the selected variant carries no plans.
    get _currentPlanId() {
      return this.frequencySelect && this.frequencySelect.value ? this.frequencySelect.value : null;
    }

    get _isSubscribe() {
      const checked = this.querySelector('[data-purchase-type]:checked');
      return checked ? checked.value === 'subscribe' : false;
    }

    // Check one of the two purchase-type radios without firing change (we drive the follow-up
    // ourselves, and a synthetic event here would re-enter _onPurchaseTypeChange mid-update).
    _selectPurchaseType(value) {
      const radio = this.querySelector('[data-purchase-type="' + value + '"]');
      if (radio && !radio.checked) radio.checked = true;
    }

    _onPurchaseTypeChange() {
      // an explicit choice, so any earlier forced fallback is no longer ours to undo
      this.forcedOneTime = false;
      this._syncSellingPlan();
    }

    // Changing frequency implies choosing Auto-Ship. Without this the dropdown reads as live while
    // One-Time is selected and nothing the customer picks there ever reaches the cart.
    _onFrequencyChange() {
      this._selectPurchaseType('subscribe');
      this.forcedOneTime = false;
      this._rememberFrequency();
      this._syncSellingPlan();
      this._updateBreakdown();
      this._refreshPrices();
    }

    // Reveal the discount breakdown block for the currently selected plan, hide the rest, and refill
    // the one value on those lines that is not static text.
    //
    // A fixed-amount adjustment is shown as a percentage ("20% off first order"), and that percentage
    // is relative to the variant's own price — $20.00 is 20% off $99.99 and 28% off $69.99 — while the
    // snippet renders one breakdown block per plan, shared by every variant on it. So each such value
    // sits in a [data-adj-percent] span keyed by its adjustment index and is filled from the current
    // variant's entry here. Blocks with no entry are ones this variant has no allocation on, so they
    // are hidden by the line above; they are cleared rather than left showing another variant's figure.
    _updateBreakdown() {
      const planId = this._currentPlanId;
      const variantPrices = this.prices[this.variantId] || {};

      this.querySelectorAll('[data-breakdown-for]').forEach((el) => {
        const blockPlanId = el.getAttribute('data-breakdown-for');
        el.hidden = blockPlanId !== planId;

        const entry = variantPrices[blockPlanId];
        const percents = (entry && entry.percents) || [];
        el.querySelectorAll('[data-adj-percent]').forEach((span) => {
          span.textContent = percents[Number(span.getAttribute('data-adj-percent'))] || '';
        });
      });
    }

    _onVariantChanged(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      this.variantId = String(variant.id);

      // Plans attach per variant, so a variant change can swap the plan ids, hop to another selling
      // plan group or land on a variant with no plans at all. Rebuild the dropdown first so every
      // step after it reads the new variant's plans, then re-derive everything from that.
      this._rebuildFrequencyOptions();
      this._updateAvailability();
      this._syncSellingPlan();
      this._updateBreakdown();
      this._refreshPrices();
    }

    // Note the chosen delivery label on the instance. It cannot be read back off the <select> when
    // it is needed: passing through a variant with no plans empties the dropdown, and the customer's
    // frequency would silently reset to the first option on the way back out.
    _rememberFrequency() {
      const selected = this.frequencySelect && this.frequencySelect.selectedOptions[0];
      if (selected) this.selectedLabel = selected.textContent.trim();
    }

    // Repopulate "Ships every" from the active variant's plans. Variants can sit in different
    // selling plan groups (percentage vs fixed-amount), so plan ids do not survive the hop and the
    // customer's chosen frequency has to be re-matched by label. Both groups carry identical
    // delivery labels by design, so this keeps the switch invisible; index then first are fallbacks
    // for the case where the labels do differ.
    _rebuildFrequencyOptions() {
      if (!this.frequencySelect) return;

      const previousIndex = this.frequencySelect.selectedIndex;
      const entry = this.prices[this.variantId];
      const plans = (entry && entry.plans) || [];

      this.frequencySelect.textContent = '';
      plans.forEach((plan) => {
        const option = document.createElement('option');
        option.value = plan.id;
        option.textContent = plan.label;
        this.frequencySelect.appendChild(option);
      });
      // keep selectedLabel on an empty rebuild so the choice survives a detour through a
      // non-subscribable variant
      if (!plans.length) return;

      let index = plans.findIndex((plan) => plan.label === this.selectedLabel);
      if (index === -1) index = previousIndex >= 0 && previousIndex < plans.length ? previousIndex : 0;
      this.frequencySelect.selectedIndex = index;
      this._rememberFrequency();
    }

    // Hide the whole chooser on a variant with no plans. Leaving it up would offer a subscription
    // the variant cannot be bought on, and add to cart fails on an invalid selling plan.
    _updateAvailability() {
      const hasPlans = this._currentPlanId != null;
      this.hidden = !hasPlans;

      if (!hasPlans) {
        if (this._isSubscribe) {
          this.forcedOneTime = true;
          this._selectPurchaseType('onetime');
        }
        return;
      }

      // Back on a subscribable variant: restore the Auto-Ship default, but only where one-time was
      // our fallback rather than the customer's pick.
      if (this.forcedOneTime) {
        this.forcedOneTime = false;
        this._selectPurchaseType('subscribe');
      }
    }

    // Empty value = one-time; otherwise the selected frequency plan id.
    _syncSellingPlan() {
      // The publish sits OUTSIDE this guard on purpose. A missing hidden input means the form submits
      // no plan at all, so one-time is exactly what the header should be showing — putting the publish
      // in the guard's shadow instead left the header stuck on whatever it last displayed.
      if (this.hiddenInput) {
        this.hiddenInput.value = this._isSubscribe && this._currentPlanId ? this._currentPlanId : '';
      }
      this._publishSelection();
    }

    // What the customer has currently chosen, for surfaces outside this component — the price under the
    // product name mirrors it (see applyHeaderPricing below). `subscribed` is false both when One-Time is
    // chosen and when the variant carries no plans at all, which is the same condition that empties the
    // hidden input above: whatever ends up in the cart is what the header shows.
    getSelection() {
      // hiddenInput is part of the condition, not just of the write above: with no input to carry it
      // the form submits no plan whatever the radios say, and the header must follow the cart.
      const planId =
        this.hiddenInput && this._isSubscribe && this._currentPlanId ? this._currentPlanId : null;
      const variantPrices = this.prices[this.variantId] || {};
      // the entry behind whichever card is selected, so `unit` below is that card's own "each"
      const entry = planId ? variantPrices[planId] : variantPrices.onetime;

      return {
        subscribed: planId != null,
        price: entry && entry.price != null ? entry.price : null,
        amount: entry && entry.amount != null ? entry.amount : null,
        // the one-time price, struck through beside the subscribe price; "" when the plan saves nothing
        compare: entry && entry.compare ? entry.compare : null,
        // Already reads "$0.97 each" — it is the card's rendered text, from the variant's own
        // unit_price. Null where the merchant set none, which is what the pack-count fallback is for.
        unit: entry && entry.unit ? entry.unit : null,
        // The delivery label of the chosen plan ("Every 1 Week"). It is the label rather than the id
        // because the Amount tiles price the OTHER variants off it too, and plan ids do not survive a
        // hop between selling plan groups where the labels do (see _rebuildFrequencyOptions).
        planLabel: planId ? this.selectedLabel : null,
      };
    }

    // Announced from _syncSellingPlan because that is the one call every state change funnels through —
    // purchase type, frequency, variant, and the initial render — so no path can move the cart price
    // without the header hearing about it.
    _publishSelection() {
      this.dispatchEvent(
        new CustomEvent('subscription:changed', { bubbles: true, detail: this.getSelection() })
      );
    }

    // Update every price / unit-price element for the active variant. Elements keyed "__current"
    // follow the frequency dropdown; other keys ("onetime", or a fixed plan id) are looked up as-is.
    // Every branch below clears on a miss rather than skipping: a variant with no plans, or one
    // whose plans belong to another group, has no entry for the previous key, and leaving the old
    // text in place would show the previous variant's price with no strikethrough beside it.
    _refreshPrices() {
      const variantPrices = this.prices[this.variantId] || {};

      const resolve = (key) => (key === '__current' ? this._currentPlanId : key);

      this.querySelectorAll('[data-plan-price]').forEach((el) => {
        const entry = variantPrices[resolve(el.getAttribute('data-plan-price'))];
        el.textContent = entry && entry.price != null ? entry.price : '';
      });

      // The card's "each". Blank in the map unless the merchant set a unit_price on the variant, which
      // these products do not carry — so it falls back to the pack count, the same figure the header and
      // the tile beside it show. Filled here rather than in the snippet because the pack count is parsed
      // in one place only, and that place is snippets/product-form.liquid.
      const formId = this.getAttribute('form-id');
      this.querySelectorAll('[data-plan-unit]').forEach((el) => {
        const entry = variantPrices[resolve(el.getAttribute('data-plan-unit'))];
        el.textContent = rowUnitPrice(formId, entry && entry.unit, entry && entry.amount);
      });

      // Struck-through compare price (the one-time price shown next to the subscribe price).
      this.querySelectorAll('[data-plan-compare]').forEach((el) => {
        const entry = variantPrices[resolve(el.getAttribute('data-plan-compare'))];
        el.textContent = entry && entry.compare ? entry.compare : '';
      });

      // "Save X%" badge — follows the frequency's savings, describes the Auto-Ship card so it stays
      // visible regardless of which purchase option is selected (hidden only when there is no discount).
      // Already carries its percent sign from the snippet, which works it out per variant for a
      // fixed-amount plan too, so it is printed as-is; "" is both "no discount" and the hidden gate.
      const current = variantPrices[this._currentPlanId];
      const savings = current ? current.savings : '';
      this.querySelectorAll('[data-savings]').forEach((el) => {
        el.textContent = savings || '';
      });
      this.querySelectorAll('[data-savings-badge]').forEach((el) => {
        el.hidden = !savings;
      });
    }

    // "Expected delivery" window in business days from today, skipping weekends. The min/max come from
    // the eta-min / eta-max attributes (product metafields custom.delivery_min_days / _max_days), and
    // fall back to 3–5 when unset or invalid.
    _renderEta() {
      const range = this.querySelector('[data-eta-range]');
      const line = this.querySelector('[data-eta]');
      if (!range || !line) return;

      const clampDays = (attr, fallback) => {
        const n = parseInt(this.getAttribute(attr), 10);
        return Number.isFinite(n) && n > 0 ? n : fallback;
      };
      let min = clampDays('eta-min', 3);
      let max = clampDays('eta-max', 5);
      if (max < min) max = min;

      const addBusinessDays = (n) => {
        const d = new Date();
        let added = 0;
        while (added < n) {
          d.setDate(d.getDate() + 1);
          const day = d.getDay();
          if (day !== 0 && day !== 6) added += 1;
        }
        return d;
      };
      const fmt = (d) =>
        d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

      range.textContent = fmt(addBusinessDays(min)) + ' – ' + fmt(addBusinessDays(max));
      line.hidden = false;
    }
  }
  window.customElements.define('subscription-options', SubscriptionOptions);
}


//fix for issue where cart addjs is called two time
//fix for issue where cart addjs is called two time
/*(function () {
  if (window.__addToCartGuard) return;
  window.__addToCartGuard = true;

  const originalFetch = window.fetch;
  let lastCartAddSignature = null;
  let lastCartAddTime = 0;

  window.fetch = function (...args) {
    const url = args[0];

    if (
      typeof url === 'string' &&
      url.includes('/cart/add')
    ) {
      const now = Date.now();
      
      // Create a signature of this request to detect true duplicates
      let signature = url;
      if (args[1] && args[1].body) {
        // For FormData or JSON body, create a simple signature
        if (args[1].body instanceof FormData) {
          // Try to get variant ID from FormData
          const variantId = args[1].body.get('id');
          signature += '_formdata_' + variantId;
        } else {
          signature += '_' + JSON.stringify(args[1].body);
        }
      }

      // Block only if it's the SAME request within 500ms (true duplicate)
      if (
        window.__cartAddInProgress && 
        signature === lastCartAddSignature && 
        now - lastCartAddTime < 500
      ) {
        console.warn('Duplicate /cart/add blocked:', signature);
        return Promise.resolve(new Response(JSON.stringify({status: 'blocked'}), { 
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        }));
      }

      console.log('Cart add allowed:', signature);
      window.__cartAddInProgress = true;
      lastCartAddSignature = signature;
      lastCartAddTime = now;

      return originalFetch.apply(this, args)
        .finally(() => {
          setTimeout(() => {
            window.__cartAddInProgress = false;
            console.log('Cart add guard released');
          }, 500);
        });
    }

    return originalFetch.apply(this, args);
  };
})();*/
/**
 * The id of the form a `variant:changed` event came from, or null if it did not come from an element.
 *
 * Every listener below matches the event back to its own product-variants / product-meta block by form id,
 * and the obvious `form.id` is a trap here. The product form carries a hidden <input name="id"> for the
 * variant, and HTMLFormElement is [LegacyOverrideBuiltIns], so its named controls shadow the built-in IDL
 * attributes: `form.id` returns that input element rather than the string. String-compared it never matches
 * and concatenated into a selector it becomes "[object HTMLInputElement]", so in both shapes the listener
 * silently does nothing on every variant change.
 */
function formIdOf(target) {
  return target && target.getAttribute ? target.getAttribute('id') : null;
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * COMBINED VARIANT PICKER
 * ------------------------------------------------------------------------------------------------------------
 *
 * When a product has the `custom.combined_variant_picker` metafield enabled, the variant picker renders every variant
 * as a single selectable block combining all its options (see snippets/product-form.liquid). Selecting a block
 * proxies to the theme's native <product-variants>.selectVariant(), which updates price, media, availability,
 * add-to-cart and the URL — and syncs the hidden per-option selects.
 *
 * The `data-variant-block` attribute below is the DOM contract with that markup — not the retired
 * `custom.variant_blocks` metafield, which nothing reads any more.
 */
document.addEventListener('change', function (event) {
  const input = event.target;
  if (!input || !input.matches || !input.matches('input[data-variant-block]')) {
    return;
  }

  const productVariants = input.closest('product-variants');
  if (productVariants && typeof productVariants.selectVariant === 'function') {
    productVariants.selectVariant(parseInt(input.value, 10));
  }
});

// Keep the checked block in sync when the variant is changed elsewhere (e.g. sticky form, auto-selection).
// getAttribute('id'), not form.id — see formIdOf below.
document.addEventListener('variant:changed', function (event) {
  const variant = event.detail && event.detail.variant;
  const formId = formIdOf(event.target);
  if (!variant || !formId) {
    return;
  }

  document
    .querySelectorAll('product-variants[form-id="' + formId + '"] input[data-variant-block]')
    .forEach(function (input) {
      input.checked = parseInt(input.value, 10) === variant.id;
    });
});

// What <subscription-options> has published for each form — Auto-Ship vs One-Time, the chosen frequency
// and its prices. Read by both surfaces that have to show the price the cart will charge: the Amount tiles
// and the header price under the product name. Keyed by form id; empty until the component publishes.
const subscriptionSelections = {};

/**
 * ------------------------------------------------------------------------------------------------------------
 * QUANTITY TILES + DYNAMIC "BEST VALUE"
 * ------------------------------------------------------------------------------------------------------------
 *
 * When the picker is split into a Size selector + a priced Quantity selector (see snippets/product-form.liquid),
 * each tile stands for the variant on screen with its Amount swapped for that tile's, so its price moves with
 * every OTHER option AND with the purchase type: the tile shows what the form would charge for that pack, which
 * is the Auto-Ship price for the chosen frequency while Auto-Ship is selected.
 * Anything less left every tile on the one-time price while the header under the product name showed the
 * Auto-Ship one — two different prices for the same pack, on the same screen.
 *
 * This re-prices every tile from the "<size>::<qty>" map and re-badges the pack with the lowest per-unit price
 * (shown price / pack count) whenever the size, the purchase type or the frequency changes.
 */

// The tile's entry for the frequency the customer is on. Plans are keyed by delivery label, not by id,
// because two tiles can be variants in different selling plan groups: the ids differ there, the labels are
// identical by design. A variant carrying plans but not that label falls back to its first — the same thing
// the frequency dropdown itself does on a group hop (_rebuildFrequencyOptions). Null means "no plan applies",
// and the caller shows the one-time price.
function tilePlanEntry(entry, planLabel) {
  const plans = entry && entry.plans;
  if (!plans) {
    return null;
  }
  if (planLabel && plans[planLabel]) {
    return plans[planLabel];
  }
  const first = Object.keys(plans)[0];
  return first ? plans[first] : null;
}

// { qtyIndex, variants: [{ id, options, price, ... }] }, or null when this <product-variants> is not the
// tiled picker (the sticky form renders one carrying no map at all).
function tileMapOf(productVariants) {
  const dataEl = productVariants.querySelector('.quantity-tile-data');
  if (!dataEl) {
    return null;
  }
  try {
    const map = JSON.parse(dataEl.textContent);
    return map && Array.isArray(map.variants) ? map : null;
  } catch (e) {
    return null;
  }
}

// The entry for the variant the form would submit right now. Read off the form's own `id` input rather
// than off the pickers: it is the value that goes in the cart, the theme keeps it current on every
// variant change, and it is already right on the first paint — where no variant:changed has fired yet.
function currentTileEntry(map, formId) {
  const form = document.getElementById(formId);
  // form.elements, not querySelector: the input is form-associated and need not be a descendant. A product
  // page carries a second `id` input for the installment form, but that belongs to a form of its own.
  const idInput = form && form.elements ? form.elements.namedItem('id') : null;
  if (!idInput) {
    return null;
  }
  const id = String(idInput.value);
  return (
    map.variants.filter(function (v) {
      return String(v.id) === id;
    })[0] || null
  );
}

// The variant this tile stands for: the current one with the Amount swapped for the tile's own value,
// every other option held. Null when that combination does not exist, which is how a tile gets hidden —
// the Amount option pools packs from the whole product, so most of them do not apply to a given
// Size / Absorbency. Matching every other option is the point: on a product with a third option, size
// alone named several variants at different prices (see snippets/product-form.liquid).
function tileEntryOf(map, current, qtyValue) {
  if (!current) {
    return null;
  }
  return (
    map.variants.filter(function (v) {
      return (
        v.options[map.qtyIndex] === qtyValue &&
        v.options.every(function (option, index) {
          return index === map.qtyIndex || option === current.options[index];
        })
      );
    })[0] || null
  );
}

function syncQuantityTiles(productVariants) {
  const map = tileMapOf(productVariants);
  const qtyContainer = productVariants.querySelector('[data-quantity-selector]');
  if (!map || !qtyContainer) {
    return;
  }

  const formId = productVariants.getAttribute('form-id');
  // Nothing to anchor the tiles to. The server render is already right, so leave it alone rather than
  // paint every tile from a variant we are guessing at.
  const current = currentTileEntry(map, formId);
  if (!current) {
    return;
  }

  // Which basis the tiles are priced on. Before <subscription-options> has published anything the server
  // render is already on the Auto-Ship price (snippets/product-form.liquid), so that is the default here
  // too — starting from one-time would repaint every tile with a price the form would not charge, the flash
  // the header render was fixed to avoid. A product with no plans carries no `plans` in the map, so it is
  // unaffected either way.
  const selection = subscriptionSelections[formId];
  const subscribed = selection ? selection.subscribed : true;
  const planLabel = selection ? selection.planLabel : null;

  // First pass: price every tile off its own variant and find the lowest per-unit pack. Per-unit values
  // come straight from the map (Liquid parses the pack count out of the option value once), so the server
  // render and this pass always agree.
  let bestTile = null;
  let bestUnit = Infinity;
  qtyContainer.querySelectorAll('[data-qty-price]').forEach(function (priceEl) {
    const entry = tileEntryOf(map, current, priceEl.getAttribute('data-qty-value'));
    const tile = priceEl.closest('[data-qty-tile]');

    if (!entry) {
      if (tile) {
        tile.hidden = true;
      }
      return;
    }
    if (tile) {
      tile.hidden = false;
    }

    // The plan entry while Auto-Ship is selected, the one-time entry otherwise — and the one-time entry
    // whenever this particular variant has no plan to show, which is also what the cart would charge for it.
    const shown = (subscribed && tilePlanEntry(entry, planLabel)) || entry;

    const totalEl = priceEl.querySelector('[data-qty-price-total]');
    const unitEl = priceEl.querySelector('[data-qty-price-unit]');
    if (totalEl) {
      totalEl.textContent = shown.price;
    }
    if (unitEl) {
      unitEl.textContent = shown.unit ? '(' + shown.unit + ' ea)' : '';
      unitEl.hidden = !shown.unit;
    }

    if (shown.unitAmount != null && shown.unitAmount < bestUnit) {
      bestUnit = shown.unitAmount;
      bestTile = priceEl.closest('.block-swatch');
    }
  });

  // Second pass: show the "Best Value" badge only on the winning tile.
  qtyContainer.querySelectorAll('[data-best-value]').forEach(function (badge) {
    badge.hidden = badge.closest('.block-swatch') !== bestTile;
  });
}

// getAttribute('id'), not form.id — see formIdOf above.
document.addEventListener('variant:changed', function (event) {
  const formId = formIdOf(event.target);
  if (!formId) {
    return;
  }
  document
    .querySelectorAll('product-variants[form-id="' + formId + '"]')
    .forEach(syncQuantityTiles);
});

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('product-variants').forEach(syncQuantityTiles);
});

/**
 * ------------------------------------------------------------------------------------------------------------
 * HEADER PRICE + PER-UNIT PRICE (under the product name)
 * ------------------------------------------------------------------------------------------------------------
 *
 * The subscription buy box shows the price the customer is actually buying at, under the product name, with
 * its per-unit price beside it (see snippets/product-info.liquid). Two things move it: the Size / Amount
 * tiles, and the Auto-Ship / One-Time choice.
 *
 * The Size and Amount tiles need no code for the money value. Choosing either is a variant change, and
 * <product-meta> rewrites the price on every one (assets/theme.js, ProductMeta._updatePrices). So the
 * one-time price is left to the theme, and the Auto-Ship price is a SECOND element that stands it down —
 * writing into the theme's element would be undone by the next variant change. Both carry the price and its
 * struck-through compare price on one line, with the per-unit price on the line below.
 *
 * Which of the two is showing is rendered server-side (snippets/product-info.liquid), so the header matches
 * the hidden selling_plan input from the first paint. Nothing here runs until <subscription-options>
 * publishes a selection; this file only ever moves the header OFF a state the server already got right.
 *
 * The per-unit price goes through rowUnitPrice, the same function that fills the two cards, so the header
 * mirrors the card it is standing in for by construction. It prefers the variant's own unit_price where the
 * merchant set one and otherwise divides the row's price by the pack count in the Amount option value —
 * which the map parses once, in snippets/product-form.liquid, so no surface can drift from the tile.
 *
 * The subscription state arrives as `subscription:changed` rather than being read back off the component,
 * and is kept per form in `subscriptionSelections` (declared with the quantity tiles above, which price
 * themselves off the same selection) so a variant change can re-derive the per-unit price from the selection
 * already made.
 */
// The tile map and the quantity radios both live on the picker's <product-variants>. The sticky form
// renders a SECOND one for the same form carrying neither (snippets/product-sticky-form.liquid), so the
// source is picked by what it holds rather than by document order — which is all that kept the right one
// winning before, and only because main-product.liquid happens to render the sticky form first.
function tileSourceFor(formId) {
  const candidates = document.querySelectorAll('product-variants[form-id="' + formId + '"]');
  for (let i = 0; i < candidates.length; i++) {
    if (
      candidates[i].querySelector('.quantity-tile-data') &&
      candidates[i].querySelector('[data-quantity-selector]')
    ) {
      return candidates[i];
    }
  }
  return null;
}

// The selected variant's own tile entry, for the per-unit price below. Same lookup the tiles use, so
// everything divides the same pack count into the same price the tile beside it shows.
function tileEntryFor(formId) {
  const productVariants = tileSourceFor(formId);
  const map = productVariants && tileMapOf(productVariants);
  return map ? currentTileEntry(map, formId) : null;
}

// The per-unit price for one row, in the form the cards and the header both print it: "$0.21 each".
//
// The merchant's own unit_price where there is one — it is set per variant in the admin, so it can differ
// between two variants that share a price and a pack count, and nothing derived could follow that. Where
// there is none, which is every product this layout ships on, the pack count out of the Amount option
// value divides the row's own price, rounded and formatted exactly as the tile's per-unit price is in
// Liquid. One function for the Auto-Ship card, the One-Time card and the header price, so no two of them
// can print different figures for the same money.
function rowUnitPrice(formId, unit, amount) {
  if (unit) {
    return unit;
  }
  const tile = tileEntryFor(formId);
  const pack = tile && tile.pack;
  if (!(pack > 0) || amount == null || typeof formatMoney !== 'function') {
    return '';
  }
  return formatMoney(Math.round(amount / pack)) + ' each';
}

// Keyed on the header itself rather than on <product-variants>. The tiles are only one of the pickers the
// subscription layout can render — a single-variant product, or a sold-out one in a section set to hide
// sold-out variants, renders none at all (see the `unless` in snippets/product-form.liquid) — while the
// header is there whenever the layout is, and it is the header that has to agree with the cart.
function applyHeaderPricing(formId) {
  if (!formId) {
    return;
  }

  const meta = document.querySelector('product-meta[form-id="' + formId + '"]');
  // Absent outside the subscription layout, which is the gate for this whole behaviour.
  const unitEl = meta && meta.querySelector('[data-header-unit-price]');
  if (!unitEl) {
    return;
  }

  // Nothing published yet. The server render already shows the price this form would submit
  // (snippets/product-info.liquid renders the Auto-Ship price directly when Auto-Ship is the default),
  // so there is nothing to improve on here and writing a default in would flash the one-time price on
  // every subscription PDP — or leave it showing for good if the component never upgrades.
  const selection = subscriptionSelections[formId];
  if (!selection) {
    return;
  }

  const subscriptionEl = meta.querySelector('[data-header-subscription-price]');
  const listEl = meta.querySelector('[data-product-price-list]');

  const showSubscription = !!(selection.subscribed && selection.price && subscriptionEl);

  // Swap which price is on show. Never touch the theme's element beyond the class below: _updatePrices
  // rewrites its innerHTML and clears its inline display on every variant change, but leaves the
  // element's own attributes alone.
  if (subscriptionEl) {
    const amountEl = subscriptionEl.querySelector('[data-header-subscription-amount]');
    const compareEl = subscriptionEl.querySelector('[data-header-subscription-compare]');
    if (amountEl) {
      amountEl.textContent = showSubscription ? selection.price : '';
    }
    // Struck-through one-time price. Gated on the plan actually saving something, the same way the card's
    // own strikethrough is, so a plan at full price does not show a line through its own price.
    if (compareEl) {
      compareEl.textContent = showSubscription && selection.compare ? selection.compare : '';
    }
    subscriptionEl.hidden = !showSubscription;
  }
  // A class, not `hidden`: [data-product-label-list] is INSIDE this element, and hiding the whole thing
  // took "Sold out" with it. The rule hides every child but .price-meta (snippets/product-info.liquid).
  if (listEl) {
    listEl.classList.toggle('price-list--header-replaced', showSubscription);
  }

  // The same "each" the selected card prints, through the same function — the header mirrors the card
  // by construction rather than by two code paths agreeing.
  const unit = rowUnitPrice(formId, selection.unit, selection.amount);

  unitEl.textContent = unit;
  unitEl.hidden = !unit;
}

document.addEventListener('subscription:changed', function (event) {
  const formId = event.target && event.target.getAttribute('form-id');
  if (!formId) {
    return;
  }
  subscriptionSelections[formId] = event.detail;
  // Tiles first, then the header: applyHeaderPricing reads a tile entry for its per-unit fallback, and
  // the two have to print the same figure.
  document
    .querySelectorAll('product-variants[form-id="' + formId + '"]')
    .forEach(syncQuantityTiles);
  applyHeaderPricing(formId);
});

// getAttribute('id'), not form.id — see formIdOf above. <subscription-options> republishes on every
// variant change of its own accord, so this is the belt to that braces: it re-derives the per-unit price
// from the tiles for a form whose component is present but not listening.
document.addEventListener('variant:changed', function (event) {
  applyHeaderPricing(formIdOf(event.target));
});

// No initial pass. <subscription-options> publishes its opening selection from connectedCallback, which
// is the first thing that can tell this file which price the form will submit, and until then the server
// render is already right. An earlier version seeded the selections here by calling getSelection() on each
// component — that could never have worked: customElements.define queues its upgrades on a microtask, so
// nothing is upgraded yet while this file is still evaluating.
